/**
 * Google Translate <-> React DOM reconciliation guard.
 *
 * Google Translate translates a page by replacing the text nodes React renders
 * with its own <font>…</font> wrapper nodes. React keeps references to the
 * original nodes and assumes it still owns the DOM tree. When React later
 * unmounts, reorders, or swaps that content it calls
 *   parent.removeChild(child)  /  parent.insertBefore(node, referenceNode)
 * but the target has been moved inside a Google Translate wrapper, so the
 * browser throws:
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * This guard makes those two operations tolerant of Google Translate's
 * mutations: when a node is not where React expects it (because Google
 * Translate relocated it), we operate on the node's real parent instead of
 * throwing. Every other case falls through to the native implementation, so
 * standard DOM behavior and error reporting are fully preserved.
 *
 * Because it targets the underlying incompatibility rather than any single
 * screen, it fixes the crash app-wide (Search, Book Worker, etc.) in both
 * development and production while keeping Google Translate fully functional.
 *
 * Reference: https://github.com/facebook/react/issues/11538
 */

let installed = false;

export function installGoogleTranslateDomFix() {
  if (installed) return;
  if (typeof Node === 'undefined' || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // Google Translate moved this node under one of its wrappers. Remove it
      // from wherever it actually lives so React's cleanup succeeds instead of
      // throwing. If it has no parent it is already detached — nothing to do.
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was relocated by Google Translate, so we cannot
      // insert relative to it on this parent. Append instead of throwing; once
      // Google Translate re-runs, the translated layout is restored.
      if (newNode) {
        return originalInsertBefore.call(this, newNode, null);
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}

export default installGoogleTranslateDomFix;
